/**
 * ENG-10196 — what `agt integration probe <slug>` may truthfully say when it
 * cannot find the slug.
 *
 * THE DEFECT THIS REPLACES. The command read its list from
 * `POST /host/agent-integrations` and reported:
 *
 *   Integration "github" is not installed on maven. Installed: <17 slugs>
 *
 * That endpoint filters `.in('status', ['active', 'configured'])` at all three
 * scopes — it is the PROVISIONING list. The same route carries a second, wider
 * list for probing:
 *
 *   export const PROBE_RESCUE_STATUSES = ['unhealthy', 'error'] as const;
 *
 * and says why in one line: "ENG-9470: probe candidacy is a DIFFERENT question
 * from provisioning candidacy."
 *
 * So a row in `status='error'` or `'unhealthy'` is probeable and not
 * provisionable. Measured on maven, 2026-09-07: `composio/googleads` and
 * `composio/webflow` returned `verdict ok` with 28 and 59 tools, seconds after
 * being absent from that "Installed:" line.
 *
 * The list was never wrong. The SENTENCE was: it answered "is this installed"
 * with a list that answers "what does the host provision", and asserted absence
 * on the difference.
 *
 * WHY THE MESSAGE RATHER THAN THE LIST. This caller cannot see `error` /
 * `unhealthy` rows at all — the endpoint filtered them out before the CLI ever
 * had them — so there is no complete list available here to switch to. Widening
 * the endpoint is a bigger change with its own blast radius. What IS available,
 * and what ENG-10196's AC3 asks for, is to stop asserting absence: name the list
 * being shown, and say what it does not cover.
 *
 * WHY IT IS LOUD ABOUT ITS OWN LIMITS. The list reads as authoritative BECAUSE
 * it is specific. In the incident that surfaced this it was used to tell a
 * person github was not installed, and to rule out a hypothesis. Both rested on
 * a list that could not support them. A confidently-worded wrong answer is worse
 * than an error, because an error prompts a second look.
 */

export interface NotProvisionedMessageInput {
  /** The slug the operator asked about, verbatim. */
  slug: string;
  /** The agent's code name. */
  agentCodeName: string;
  /** definition_ids from POST /host/agent-integrations — the PROVISIONED set. */
  provisioned: readonly string[];
}

/**
 * Pure so the wording is pinned by a test rather than by whoever edits the
 * command next. Returns the whole multi-line message.
 */
export function notProvisionedMessage(input: NotProvisionedMessageInput): string {
  const list = input.provisioned.length > 0 ? input.provisioned.join(', ') : 'none';
  return [
    `Integration "${input.slug}" is not in ${input.agentCodeName}'s PROVISIONED set.`,
    `Provisioned (status active/configured): ${list}`,
    '',
    // The load-bearing sentence. Without it the line above is read as "not
    // installed", which is the claim this function exists to stop making.
    `This is NOT proof that "${input.slug}" is absent. Rows in status 'error' or`,
    `'unhealthy' are excluded from this list and are still probeable (ENG-9470),`,
    `so an integration can be present, serving tools, and missing from the line`,
    `above. To settle it, read the agent's integration rows directly rather than`,
    `relying on this enumeration.`,
  ].join('\n');
}
